import React, { useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined, SearchOutlined } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    padding: theme.spacing(2),
    flex: 1,
    minHeight: 0,
  },
  tableWrap: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
  },
  options: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    "& + &": {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  optionTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  optionHelp: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  agentsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  agentCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  avatar: {
    width: 28,
    height: 28,
    fontSize: "0.8rem",
  },
  onlineDot: {
    "& .MuiBadge-badge": {
      width: 9,
      height: 9,
      minWidth: 9,
      borderRadius: "50%",
      border: `2px solid ${theme.palette.background.paper}`,
    },
  },
  empty: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    padding: theme.spacing(2),
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  headerWithHelp: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  helpIcon: {
    fontSize: 15,
    cursor: "help",
    color: theme.palette.text.disabled,
  },
}));

const OptionRow = ({ classes, title, help, checked, disabled, onChange }) => (
  <div className={classes.optionRow}>
    <div>
      <Typography className={classes.optionTitle}>{title}</Typography>
      <Typography className={classes.optionHelp}>{help}</Typography>
    </div>
    <Switch checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} color="primary" />
  </div>
);

const HeaderWithHelp = ({ classes, label, help }) => (
  <span className={classes.headerWithHelp}>
    {label}
    <Tooltip title={help} arrow placement="top">
      <InfoOutlined className={classes.helpIcon} />
    </Tooltip>
  </span>
);

const QueueAssignmentTab = ({ settings, onSettingsChange, agents, onAgentsChange, onApply }) => {
  const { classes } = useStyles();
  const [search, setSearch] = useState("");

  const filteredAgents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return agents;
    return agents.filter(
      (agent) => agent.name?.toLowerCase().includes(term) || agent.email?.toLowerCase().includes(term)
    );
  }, [agents, search]);

  const setSetting = (key) => (value) => onSettingsChange({ ...settings, [key]: value });

  const updateAgent = (agentId, changes) =>
    onAgentsChange(agents.map((agent) => (agent.id === agentId ? { ...agent, ...changes } : agent)));

  return (
    <Box className={classes.root}>
      <Paper variant="outlined" className={classes.options}>
        <OptionRow
          classes={classes}
          title={i18n.t("queueModal.assignment.autoAssignTitle")}
          help={i18n.t("queueModal.assignment.autoAssignHelp")}
          checked={settings.autoAssignEnabled}
          onChange={setSetting("autoAssignEnabled")}
        />
        <OptionRow
          classes={classes}
          title={i18n.t("queueModal.assignment.offlineTitle")}
          help={i18n.t("queueModal.assignment.offlineHelp")}
          checked={settings.autoAssignOffline}
          disabled={!settings.autoAssignEnabled}
          onChange={setSetting("autoAssignOffline")}
        />
        <OptionRow
          classes={classes}
          title={i18n.t("queueModal.assignment.redistributeTitle")}
          help={i18n.t("queueModal.assignment.redistributeHelp")}
          checked={settings.redistributeUnavailable}
          onChange={setSetting("redistributeUnavailable")}
        />
      </Paper>

      <div className={classes.agentsHeader}>
        <Typography className={classes.sectionTitle}>{i18n.t("queueModal.assignment.agents")}</Typography>
        <TextField
          size="small"
          variant="outlined"
          placeholder={i18n.t("queueModal.assignment.searchAgent")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" color="disabled" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <Paper variant="outlined" className={classes.tableWrap}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("queueModal.assignment.columns.name")}</TableCell>
              <TableCell>{i18n.t("queueModal.assignment.columns.email")}</TableCell>
              <TableCell align="center">
                <HeaderWithHelp
                  classes={classes}
                  label={i18n.t("queueModal.assignment.columns.member")}
                  help={i18n.t("queueModal.assignment.columns.memberHelp")}
                />
              </TableCell>
              <TableCell align="center">
                <HeaderWithHelp
                  classes={classes}
                  label={i18n.t("queueModal.assignment.columns.autoAssign")}
                  help={i18n.t("queueModal.assignment.columns.autoAssignHelp")}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAgents.map((agent) => (
              <TableRow key={agent.id} hover>
                <TableCell>
                  <div className={classes.agentCell}>
                    <Badge
                      overlap="circular"
                      variant="dot"
                      color={agent.online ? "success" : "default"}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      className={classes.onlineDot}
                    >
                      <Avatar className={classes.avatar}>{(agent.name || "?").charAt(0).toUpperCase()}</Avatar>
                    </Badge>
                    {agent.name}
                  </div>
                </TableCell>
                <TableCell>{agent.email}</TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    color="primary"
                    checked={agent.isMember}
                    onChange={(e) => updateAgent(agent.id, { isMember: e.target.checked })}
                  />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    color="primary"
                    checked={agent.isMember && agent.autoAssign}
                    disabled={!agent.isMember}
                    onChange={(e) => updateAgent(agent.id, { autoAssign: e.target.checked })}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filteredAgents.length === 0 && (
          <Typography className={classes.empty}>{i18n.t("queueModal.assignment.noAgents")}</Typography>
        )}
      </Paper>

      <div className={classes.actions}>
        <Button variant="contained" color="primary" onClick={onApply}>
          {i18n.t("queueModal.assignment.apply")}
        </Button>
      </div>
    </Box>
  );
};

export default QueueAssignmentTab;
